<template>
    <div class="page-container">
        <!-- 顶部导航栏 -->
        <div class="nav-bar">
            <div class="nav-back" @click="onBack">
                <span class="back-arrow">‹</span>
                <span class="back-text">返回</span>
            </div>
            <span class="nav-title">订单详情</span>
            <div class="nav-placeholder"></div>
        </div>
        <!-- 导航栏占位符，防止内容被遮挡 -->
        <div class="nav-bar-placeholder"></div>
        <!-- 第一块：可伸缩日历 -->
        <div class="calendar-section">
            <div class="calendar-header">
                <div class="calendar-nav prev" @click="onPrevMonth">
                    <span class="nav-icon">‹</span>
                </div>
                <span class="calendar-month" @click="onToggleCalendar">{{ currentYear }}年{{ currentMonth }}月</span>
                <div class="calendar-nav next" @click="onNextMonth">
                    <span class="nav-icon">›</span>
                </div>
            </div>

            <!-- 统计信息 -->
            <div class="calendar-stats">
                <div class="stat-item">
                    <span class="stat-label">预约天数</span>
                    <span class="stat-value">{{ monthStats.appointedDays }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">服务天数</span>
                    <span class="stat-value highlight">{{ monthStats.serviceDays }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">暂停天数</span>
                    <span class="stat-value">{{ monthStats.pausedDays }}</span>
                </div>
            </div>

            <!-- 日历视图 -->
            <div class="calendar-grid" v-if="calendarExpanded">
                <div class="calendar-weekdays">
                    <span class="weekday" v-for="item in weekdays" :key="item">{{ item }}</span>
                </div>
                <div class="calendar-days">
                    <div class="calendar-day" :class="{
                        'service-past': item.status === 'past',
                        'service-current': item.status === 'current',
                        'service-future': item.status === 'future',
                        'service-paused': item.status === 'paused',
                        'today': item.isToday
                    }" v-for="item in calendarDays" :key="item.key">
                        <span class="day-num">{{ item.day }}</span>
                        <span class="day-label" v-if="item.label">{{ item.label }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 第二块：轨迹+缴费混合时间线 -->
        <div class="trace-section">
            <div class="section-title">服务记录</div>
            <div class="trace-list">
                <div class="trace-item" v-for="(item, index) in mixedList" :key="index">
                    <!-- 时间线 -->
                    <div class="timeline">
                        <div
                            :class="['timeline-dot', { 'payment': item.source === 'payment', 'last': index === mixedList.length - 1 }]">
                        </div>
                        <div class="timeline-line" v-if="index < mixedList.length - 1"></div>
                    </div>

                    <!-- 轨迹内容 -->
                    <div class="trace-content">
                        <div class="trace-header">
                            <span class="trace-time">{{ item.formattedTime }}</span>
                            <div class="trace-type-tag">{{ item.typeText }}</div>
                            <div class="trace-days-tag" v-if="item.days > 0">服务天数 {{ item.days }}天</div>
                            <!-- 缴费金额标签 -->
                            <div class="payment-tag" v-if="item.source === 'payment'">已缴费 ￥{{ item.amount }}</div>
                        </div>

                        <!-- 轨迹详情 -->
                        <div class="trace-detail">
                            <div class="detail-content">
                                <div class="detail-row" v-if="item.planTypeName">
                                    <span class="detail-label">陪护服务</span>
                                    <span class="detail-value">{{ item.planTypeName || '--' }}</span>
                                </div>
                                <div class="detail-row" v-if="item.planPrice">
                                    <span class="detail-label">套餐单价</span>
                                    <span class="detail-value">{{ item.planPrice }}元/天</span>
                                </div>
                                <div class="detail-row" v-if="item.location">
                                    <span class="detail-label">服务地点</span>
                                    <span class="detail-value">{{ item.location }}</span>
                                </div>
                                <div class="detail-row" v-if="item.caregiverName">
                                    <span class="detail-label">护工</span>
                                    <span class="detail-value">{{ item.caregiverName }}</span>
                                </div>
                                <div class="detail-row" v-if="item.remark">
                                    <span class="detail-label">原因</span>
                                </div>
                                <div class="detail-row" v-if="item.remark">
                                    <span class="remark-text">{{ item.remark }}</span>
                                </div>
                            </div>
                            <div class="detail-fee">
                                <div class="fee-row" v-if="item.amount > 0">
                                    <span class="fee-label">应缴费用</span>
                                    <span class="fee-value red">¥{{ item.amount }}</span>
                                </div>
                                <div class="fee-row" v-if="item.actualAmount > 0">
                                    <span class="fee-label">实际支付</span>
                                    <span class="fee-value green">¥{{ item.actualAmount }}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <!-- 第三块：订单原始信息 + 多退少补按钮 -->
        <div class="order-detail-section">
            <div class="section-title">订单详情</div>
            <div class="detail-card">
                <div class="detail-row">
                    <span class="detail-label">订单编号</span>
                    <span class="detail-value">{{ order.orderNo }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">开始时间</span>
                    <span class="detail-value">{{ order.startTime }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">结束时间</span>
                    <span class="detail-value">{{ order.endTime }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">陪护服务</span>
                    <span class="detail-value">{{ (order.caregiverPlan && order.caregiverPlan.name) || '--' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">套餐服务</span>
                    <span class="detail-value">￥ {{ (order.caregiverPlan && order.caregiverPlan.price) || 0 }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">天数</span>
                    <span class="detail-value">￥ {{ order.serviceDays }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">服务时长</span>
                    <span class="detail-value">{{ order.actualDays }} 天</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">被陪护人</span>
                    <div class="detail-value patient-info">
                        <span>{{ order.patientName }}</span>
                        <span class="patient-tag" v-if="order.patientAge">{{ order.patientAge }}岁</span>
                        <span class="patient-tag" v-if="order.patientGender">{{ order.patientGender }}</span>
                    </div>
                </div>
                <div class="detail-row">
                    <span class="detail-label">科室</span>
                    <span class="detail-value">{{ order.divisionName }} {{ order.bedNumberCn }}</span>
                </div>
            </div>
        </div>

        <div class="order-detail-section">
            <div class="section-title">费用详情</div>
            <div class="detail-card">
                <div class="detail-row">
                    <span class="detail-label">订单合计</span>
                    <span class="detail-value price">￥{{ order.formattedOrderAmount || '0.00' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">实付金额</span>
                    <span class="detail-value green">￥{{ order.formattedReceivedAmount || '0.00' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">未缴金额</span>
                    <span class="detail-value red">￥{{ order.formattedPendingAmount || '0.00' }}</span>
                    <span class="fee-hint">(负数代表未欠费)</span>

                </div>
                <div class="fee-note">
                    <span>注: (未缴金额 = 合计金额 - 实付金额)</span>
                </div>
            </div>
        </div>

        <div class="order-detail-section" v-if="order.isPaid === undefined && order.isWait === undefined">
            <!-- 多退少补按钮 -->
            <div class="payTypes" v-if="order.isPaid === undefined && order.isWait === undefined">
                <div class="payType-title">支付方式</div>
                <div>
                    <el-radio-group v-model="radio" style="width: 100%">
                        <div>
                            <div class="payType-radio payType-bottom">
                                <el-radio :label="1" class="custom-radio">
                                    <el-image class="imgIcon"
                                        :src="require('../../assets/imgs/nonghang.png')"></el-image>
                                    中国农业银行
                                </el-radio>
                            </div>
                            <div class="payType-radio payType-bottom">
                                <el-radio :label="2">
                                    <el-image class="imgIcon" :src="require('../../assets/imgs/wxzf.jpeg')"></el-image>
                                    微信支付
                                </el-radio>
                            </div>
                        </div>
                    </el-radio-group>
                </div>
            </div>

            <div class="action-btn-area">
                <button class="refund-btn" v-if="order.isWait">
                    等待支付结果
                </button>
                <button class="refund-btn" v-else-if="order.isPaid === undefined && order.isWait === undefined"
                    @click="orderAdd">
                    去缴费
                </button>
                <!-- <button class="refund-btn green" v-else-if="(order.settleAmount|| order.amount) < order.paidAmount" @click="goToFeature">
                        申请退款
                    </button> -->
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'orderDetailProgress',
    data() {
        return {
            orderId: '',
            order: {},
            // 轨迹列表
            traceList: [],
            // 日历相关
            calendarExpanded: true,
            currentYear: 2025,
            currentMonth: 9,
            weekdays: ['日', '一', '二', '三', '四', '五', '六'],
            calendarDays: [],
            monthStats: {
                appointedDays: 0,
                serviceDays: 0,
                pausedDays: 0
            },
            // 混合后的轨迹+缴费列表
            mixedList: [],
            totalPaidAmount: 0,
            differenceAmount: 0,
            // 二维码弹窗 & 支付轮询
            showQRModal: false,
            qrImageUrl: '',
            payStatus: -1,
            _orderCenterId: '',
            _payPollTimer: null,

            radio: 1,
        }
    },
    mounted() {
        this.initPage()
    },
    methods: {
        // 页面初始化：解析路由参数
        initPage() {
            const { orderId } = this.$route.query
            if (orderId) {
                this.orderId = orderId
                this.fetchOrderDetail(orderId)
                this.fetchTraceList(orderId)
            }
            this.initCalendar()
        },

        /**
         * 格式化退款金额：如果存在第三位小数（如0.005）则保留3位，否则保留2位
         */
        formatRefundAmount(amount) {
            const str = amount.toFixed(3)
            // 检查第三位小数是否为0
            if (str.charAt(str.length - 1) === '0') {
                return amount.toFixed(2)
            }
            return str
        },

        /**
         * 获取订单详情
         */
        async fetchOrderDetail(orderId) {
            try {
                let url = "/api-enterprise/employee/order/detail"
                let query = {
                    orderId: orderId
                }
                this.$api.post(url, query, response => {
                    if (response.data.result == "success") {
                        let orderData = response.data.data
                        if (Array.isArray(orderData) && orderData.length > 0) {
                            orderData = orderData[0]
                        }
                        if (orderData && typeof orderData === 'object') {
                            this.order = this.formatOrder(orderData)
                        }
                    }
                })

            } catch (err) {
                console.error('fetchOrderDetail fail:', err)
            }
        },

        /**
         * 获取轨迹列表 + 缴费信息，合并后按时间排序
         */
        async fetchTraceList(orderId) {
            try {
                let url = "/api-enterprise/employee/order/trace/list2"
                let query = {
                    orderId: orderId
                }
                this.$api.post(url, query, response => {
                    if (response.data.result == "success") {
                        const data = response.data.data

                        // 处理轨迹列表
                        const traces = (data.traceList || []).map(trace => ({
                            ...this.formatTrace(trace),
                            source: 'trace',
                            sortTime: trace.startTime || ''
                        }))

                        // 处理订单缴费记录（type=5）
                        const orderAmounts = (data.order_amounts || []).map(item => ({
                            source: 'payment',
                            paymentType: 'order',
                            typeText: '订单缴费',
                            amount: item.amount || 0,
                            sortTime: item.successTime || '',
                            successTime: item.successTime || ''
                        }))

                        // 处理加收费用记录（type=6）
                        const additionalAmounts = (data.additional_amounts || []).map(item => ({
                            source: 'payment',
                            paymentType: 'additional',
                            typeText: item.reason || '加收费用',
                            amount: item.amount || 0,
                            sortTime: item.successTime || '',
                            successTime: item.successTime || ''
                        }))

                        const additionalAmountsWait = (data.additional_amounts_wait || []).map(item => ({
                            source: 'paymentWait',
                            paymentType: 'additional',
                            typeText: '等待缴费结果',
                            amount: item.amount || 0,
                            sortTime: item.createTime || '',
                        }))

                        // 合并所有记录
                        const mixedList = [...traces, ...orderAmounts, ...additionalAmounts, ...additionalAmountsWait]
                        // 按时间排序
                        mixedList.sort((a, b) => {
                            if (!a.sortTime) return 1
                            if (!b.sortTime) return -1
                            return a.sortTime.localeCompare(b.sortTime)
                        })

                        // 计算总已缴金额
                        let totalPaidAmount = 0
                        orderAmounts.forEach(item => { totalPaidAmount += item.amount })
                        additionalAmounts.forEach(item => { totalPaidAmount += item.amount })

                        const settleAmount = this.order.settleAmount || 0
                        const differenceAmount = settleAmount - totalPaidAmount

                        this.mixedList = mixedList
                        this.totalPaidAmount = totalPaidAmount
                        this.differenceAmount = differenceAmount
                        this.traceList = traces

                        this.updateCalendarStats(traces)
                    }
                })


            } catch (err) {
                console.error('fetchTraceList fail:', err)
            }
        },

        /**
         * 格式化订单数据
         */
        formatOrder(order) {
            const plan = order.caregiverPlan || {}
            // 格式化金额
            const orderAmount = order.orderAmounts && order.orderAmounts.orderAmount || order.amount || 0;
            const receivedAmount = order.orderAmounts && order.orderAmounts.receivedAmount || order.paidAmount || 0;
            const refundAmount = order.orderAmounts && order.orderAmounts.refundAmount || 0;
            const pendingAmount = orderAmount - receivedAmount;
            const actualAmount = receivedAmount - refundAmount;

            return {
                ...order,
                planName: plan.name || '陪护服务',
                planPrice: plan.price || 0,
                divisionBed: order.bedNumber
                    ? (order.divisionName + order.bedNumberCn)
                    : order.divisionName || '--',
                patientName: order.patientName || '--',
                patientAge: order.patientAge || '',
                patientGender: order.patientGender || '',
                orderNo: order.orderNo || '--',
                startTime: order.startTime || '--',
                endTime: order.endTime || '--',
                actualDays: order.actualDays || 0,
                settleAmount: order.settleAmount || 0,
                settled: order.status === 100,

                // 格式化后的金额字段
                formattedOrderAmount: this.formatAmount(orderAmount),
                formattedReceivedAmount: this.formatAmount(receivedAmount),
                formattedPendingAmount: this.formatAmount(pendingAmount),
                formattedActualAmount: this.formatAmount(actualAmount),
            }
        },

        /**
  * 格式化金额，保留两位小数
  */
        formatAmount(amount) {
            if (isNaN(amount) || amount === null || amount === undefined) {
                return '0.00';
            }
            return Number(amount).toFixed(2);
        },
        /**
         * 格式化轨迹数据
         */
        formatTrace(trace) {
            const typeMap = {
                'start': '开始服务',
                'change_plan': '更换套餐',
                'change_division': '更换科室',
                'change_bed': '更换床位',
                'change_caregiver': '更换护工',
                'pause': '暂停服务',
                'resume': '恢复服务',
                'settle': '结算',
                'extra_charge': '加收费用',
                'extend': '结束服务'
            }
            const planInfo = trace.planInfo || {};

            let formattedTime = '';
            if (trace.startTime && trace.startTime.length >= 16) {
                const date = new Date(trace.startTime);
                const month = date.getMonth() + 1;
                const day = date.getDate();
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');
                formattedTime = `${month}月${day}日 ${hours}:${minutes}`;
            } else if (trace.startTime) {
                formattedTime = trace.startTime;
            }
            return {
                ...trace,
                typeText: trace.typeCn || typeMap[trace.type] || trace.type,
                days: trace.days || 0,
                pauseDays: trace.pause_days || 0,
                startTime: trace.startTime || '',
                formattedTime: formattedTime,
                actualAmount: trace.actual_amount || 0,
                amount: trace.amount || 0,
                planName: planInfo.name || '',
                planPrice: planInfo.price || 0,
                planTypeName: planInfo.typeName || '',
                planDescription: planInfo.description || '',
            }
        },

        /**
         * 初始化日历
         */
        initCalendar() {
            const now = new Date()
            this.currentYear = now.getFullYear()
            this.currentMonth = now.getMonth() + 1
            this.buildCalendar()
        },

        /**
         * 构建日历
         */
        buildCalendar() {
            const { currentYear, currentMonth } = this
            const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay()
            const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
            const today = new Date()
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

            const calendarDays = []
            // 前置空白占位
            for (let i = 0; i < firstDay; i++) {
                calendarDays.push({ day: '', date: '', key: `empty-${i}` })
            }
            // 当月日期
            for (let d = 1; d <= daysInMonth; d++) {
                const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`
                calendarDays.push({
                    day: d,
                    date: dateStr,
                    status: '',
                    label: '',
                    key: dateStr,
                    isToday: dateStr === todayStr
                })
            }
            // 后置空白占位，确保日历是完整的7列
            const remainingDays = 42 - calendarDays.length // 6行 * 7列 = 42
            for (let i = 0; i < remainingDays; i++) {
                calendarDays.push({ day: '', date: '', key: `empty-end-${i}` })
            }
            this.calendarDays = calendarDays
        },

        /**
         * 更新日历统计和状态
         */
        updateCalendarStats(traceList) {
            let serviceDays = 0
            let pausedDays = 0

            // 获取服务时间段
            const servicePeriods = this.calculateServicePeriods(traceList)

            // 更新日历每天的状态
            this.updateCalendarDayStatus(servicePeriods)

            traceList.forEach(trace => {
                if (trace.typeCn === '暂停服务' && trace.days) {
                    pausedDays += trace.days;
                } else if (trace.typeCn !== '费用结算') {
                    serviceDays += trace.days || 0;
                }
            });

            this.monthStats = {
                appointedDays: serviceDays + pausedDays,
                serviceDays: serviceDays,
                pausedDays: pausedDays
            }
        },

        /**
         * 计算服务时间段（根据 typeCn 判断）
         */
        calculateServicePeriods(traceList) {
            const periods = []

            // 按时间排序
            const sortedTraces = [...traceList].sort((a, b) => {
                return (a.startTime || '').localeCompare(b.startTime || '')
            })

            // 获取起始时间：第一个新订单的 startTime
            let startDate = null
            for (const trace of sortedTraces) {
                if (trace.typeCn === '新订单') {
                    startDate = trace.startTime
                    break
                }
            }

            // 获取结束时间：最大的 endTime，但费用结算优先级最高
            let endDate = null
            let hasSettlement = false

            for (const trace of sortedTraces) {
                if (trace.typeCn === '费用结算') {
                    endDate = trace.endTime
                    hasSettlement = true
                    break
                }
            }

            if (!hasSettlement) {
                for (const trace of sortedTraces) {
                    if (trace.endTime) {
                        if (!endDate || trace.endTime > endDate) {
                            endDate = trace.endTime
                        }
                    }
                }
            }

            // 如果有起始时间，添加服务时间段
            if (startDate) {
                periods.push({
                    start: startDate,
                    end: endDate,
                    status: 'service'
                })
            }

            // 收集所有恢复服务的时间点
            const resumeTimes = []
            for (const trace of sortedTraces) {
                if (trace.typeCn === '恢复服务' && trace.startTime) {
                    resumeTimes.push(trace.startTime)
                }
            }

            // 添加暂停时间段（考虑恢复服务的时间）
            for (const trace of sortedTraces) {
                if (trace.typeCn === '暂停服务' && trace.startTime && trace.endTime) {
                    let pauseEndTime = trace.endTime

                    // 如果恢复服务的 startTime 比暂停服务的 endTime 早，则以恢复服务的 startTime 为准
                    for (const resumeTime of resumeTimes) {
                        if (resumeTime < pauseEndTime && resumeTime > trace.startTime) {
                            pauseEndTime = resumeTime
                        }
                    }

                    periods.push({
                        start: trace.startTime,
                        end: pauseEndTime,
                        status: 'paused'
                    })
                }
            }

            return periods
        },

        /**
         * 更新日历每天的状态
         */
        updateCalendarDayStatus(servicePeriods) {
            const today = new Date()
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

            this.calendarDays.forEach(day => {
                if (!day.date) return

                let status = ''
                let label = ''

                // 先检查是否在暂停时间段内
                const pausedPeriod = servicePeriods.find(p =>
                    p.status === 'paused' &&
                    day.date >= p.start.substring(0, 10) &&
                    day.date <= p.end.substring(0, 10)
                )

                if (pausedPeriod) {
                    status = 'paused'
                    label = '暂停'
                } else {
                    // 检查是否在服务时间段内
                    const servicePeriod = servicePeriods.find(p =>
                        p.status === 'service' &&
                        day.date >= p.start.substring(0, 10) &&
                        (!p.end || day.date <= p.end.substring(0, 10))
                    )

                    if (servicePeriod) {
                        if (day.date < todayStr) {
                            status = 'past'
                            label = '已服务'
                        } else if (day.date === todayStr) {
                            status = 'current'
                            label = '服务中'
                        } else {
                            status = 'future'
                            label = '预约'
                        }
                    }
                }

                day.status = status
                day.label = label
                day.isToday = day.date === todayStr
            })
        },

        /**
         * 展开/收起日历
         */
        onToggleCalendar() {
            this.calendarExpanded = !this.calendarExpanded
        },

        /**
         * 上一个月
         */
        onPrevMonth() {
            if (this.currentMonth === 1) {
                this.currentMonth = 12
                this.currentYear--
            } else {
                this.currentMonth--
            }
            this.buildCalendar()
            // 更新日历状态（背景颜色）
            if (this.traceList && this.traceList.length > 0) {
                const servicePeriods = this.calculateServicePeriods(this.traceList)
                this.updateCalendarDayStatus(servicePeriods)
            }
        },

        /**
         * 下一个月
         */
        onNextMonth() {
            if (this.currentMonth === 12) {
                this.currentMonth = 1
                this.currentYear++
            } else {
                this.currentMonth++
            }
            this.buildCalendar()
            // 更新日历状态（背景颜色）
            if (this.traceList && this.traceList.length > 0) {
                const servicePeriods = this.calculateServicePeriods(this.traceList)
                this.updateCalendarDayStatus(servicePeriods)
            }
        },

        // ========== 去缴费 ==========
        orderAdd() {
            if (this.radio == 1) {
                let _this = this
                let url = '/api-enterprise/pay/prepare5'
                let query = {
                    paymentId: _this.order.paymentId,
                }
                if (this.order.status == 100) {
                    url = '/api-enterprise/pay/prepare3'
                    query.orderId = _this.order.id
                }

                this.$api.post(url, query, function (res) {
                    if (res.data.result == "success") {
                        let dynamicData = res.data.dynamicData
                        window.location.href = 'https://wx.abchina.com/webank/main-view/openTagForId?id=k0WoqSSxzqs%3D&dynamicData=' + dynamicData;
                    }
                })
            } else {
                let _this = this
                let url = '/api-enterprise/pay/prepare5'
                let query = {
                    paymentId: _this.order.paymentId,
                }
                if (this.order.status == 100) {
                    url = '/api-enterprise/pay/prepare3'
                    query.orderId = _this.order.id
                }

                this.$api.post(url, query, function (res) {
                    if (res.data.result == "success") {
                        let JSAPI = res.data.JSAPI
                        // 微信支付
                        WeixinJSBridge.invoke('getBrandWCPayRequest', {
                            appId: JSAPI.sub_appId,   // 上一步得到的参数
                            nonceStr: JSAPI.nonceStr, // 上一步得到的参数
                            package: JSAPI.package,   // 上一步得到的参数
                            signType: JSAPI.signType, // 上一步得到的参数
                            timeStamp: JSAPI.timeStamp,   // 上一步得到的参数
                            paySign: JSAPI.paySign    // 上一步得到的参数
                        }, res => {
                            // 根据get_brand_wcpay_request的值判断支付状态
                            if (res.err_msg === "get_brand_wcpay_request:ok") {
                                // ok：支付成功，刷新订单详情并跳转
                                _this.fetchOrderDetail(_this.orderId)
                                setTimeout(() => {
                                    _this.$router.replace({ path: '/order' })
                                }, 500)
                            } else if (res.err_msg === "get_brand_wcpay_request:cancel") {
                                // cancel：用户取消支付
                                _this.$message.warning('支付已取消')
                            } else if (res.err_msg === "get_brand_wcpay_request:fail") {
                                // fail：支付失败
                                _this.$message.error('支付失败，请重试')
                            }
                        })
                    }
                })
            }

        },

        onBack() {
            this.$router.back()
        },
        goToFeature(e) {
            Toast('功能开发中')
        },
    }
}
</script>

<style scoped>
.page-container {
    min-height: 100vh;
    background-color: #f5f7fa;
    padding-bottom: 20px;
}

/* 状态 */
.status-section {
    background-color: #fff;
    padding: 10px 15px;
}

.status-row {
    display: flex;
    align-items: center;
    gap: 5px;
}

.status-icon {
    font-size: 16px;
}

.status-text {
    font-size: 14px;
    color: #333;
}

.status-text.settled {
    color: #07c160;
    font-weight: 600;
}

/* 服务信息卡片 */
.service-card {
    background-color: #fff;
    margin: 10px 15px;
    border-radius: 6px;
    padding: 12.5px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.service-row {
    display: flex;
    align-items: center;
    gap: 7.5px;
    margin-bottom: 5px;
}

.service-type {
    font-size: 15px;
    font-weight: 600;
    color: #333;
}

.service-price {
    font-size: 13px;
    color: #07c160;
}

.pay-tag {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    margin-left: auto;
}

.pay-tag.settled {
    background-color: #e8f8f0;
    color: #07c160;
}

.service-location {
    display: flex;
    align-items: center;
    gap: 4px;
}

.location-icon {
    font-size: 14px;
}

.location-text {
    font-size: 13px;
    color: #666;
}

/* 日历区域 */
.calendar-section {
    background-color: #fff;
    margin: 10px 15px;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12.5px;
    border-bottom: 1px solid #f0f0f0;
}

.calendar-nav {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 50%;
    transition: background-color 0.2s;
}

.calendar-nav:active {
    background-color: #f0f0f0;
}

.nav-icon {
    font-size: 20px;
    color: #666;
    font-weight: 300;
}

.calendar-month {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    cursor: pointer;
}

/* 统计信息 */
.calendar-stats {
    display: flex;
    justify-content: space-around;
    padding: 12.5px;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.stat-label {
    font-size: 12px;
    color: #999;
}

.stat-value {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.stat-value.highlight {
    color: #07c160;
}

/* 日历网格 */
.calendar-grid {
    padding: 0 12.5px 12.5px;
}

.calendar-weekdays {
    display: flex;
    margin-bottom: 7.5px;
}

.weekday {
    flex: 1;
    text-align: center;
    font-size: 12px;
    color: #999;
}

.calendar-days {
    display: flex;
    flex-wrap: wrap;
}

.calendar-day {
    width: calc(100% / 7);
    height: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 6px;
    margin: 1px 0;
    box-sizing: border-box;
}

.day-num {
    font-size: 13px;
    color: #333;
    z-index: 1;
    position: relative;
}

.day-label {
    font-size: 10px;
    margin-top: 2px;
    z-index: 1;
    position: relative;
}

/* 已服务状态 - 绿色字体 */
.calendar-day.service-past {
    background-color: transparent;
}

.calendar-day.service-past .day-num {
    color: #07c160;
    font-weight: 600;
}

.calendar-day.service-past .day-label {
    color: #07c160;
}

/* 服务中状态 - 橙色字体 */
.calendar-day.service-current {
    background-color: transparent;
}

.calendar-day.service-current .day-num {
    color: #ff6600;
    font-weight: 600;
}

.calendar-day.service-current .day-label {
    color: #ff9500;
}

/* 预约状态 - 灰色字体 */
.calendar-day.service-future {
    background-color: transparent;
}

.calendar-day.service-future .day-num {
    color: #999;
    font-weight: 600;
}

.calendar-day.service-future .day-label {
    color: #999;
}

/* 暂停状态 - 灰色字体 */
.calendar-day.service-paused {
    background-color: transparent;
}

.calendar-day.service-paused .day-num {
    color: #999;
    font-weight: 600;
}

.calendar-day.service-paused .day-label {
    color: #999;
}

/* 今天样式 */
.calendar-day.today .day-num {
    background-color: #ff9500;
    color: #fff;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 轨迹区域 */
.trace-section {
    background-color: #fff;
    margin: 10px 15px;
    border-radius: 6px;
    padding: 12.5px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
}

.trace-list {
    display: flex;
    flex-direction: column;
}

.trace-item {
    display: flex;
    margin-bottom: 0;
    padding-bottom: 15px;
}

/* 时间线 */
.timeline {
    width: 25px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 10px;
}

.timeline-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #07c160;
    margin-top: 4px;
    flex-shrink: 0;
}

.timeline-dot.last {
    background-color: #07c160;
}

.timeline-dot.payment {
    background-color: #ff9500;
}

.timeline-line {
    width: 1px;
    flex: 1;
    background-color: #ddd;
    margin-top: 5px;
}

/* 轨迹内容 */
.trace-content {
    flex: 1;
}

.trace-header {
    display: flex;
    align-items: center;
    gap: 7.5px;
    margin-bottom: 4px;
}

.trace-time {
    font-size: 13px;
    color: #999;
}

.trace-days-tag {
    font-size: 11px;
    color: #07c160;
    background-color: #e8f8f0;
    padding: 2px 6px;
    border-radius: 3px;
}

.trace-type {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 5px;
}

.trace-type.payment-type {
    color: #ff9500;
}

/* 缴费标签 */
.payment-tag {
    font-size: 11px;
    color: #ff9500;
    background-color: #fffbe6;
    padding: 2px 6px;
    border-radius: 3px;
    margin-left: auto;
}

/* 服务详情 */
.trace-detail {
    background-color: #f9f9f9;
    border-radius: 4px;
    padding: 7.5px;
    margin-bottom: 5px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.detail-row:last-child {
    margin-bottom: 0;
}

.detail-label {
    font-size: 12px;
    color: #999;
}

.detail-value {
    font-size: 12px;
    color: #333;
}

/* 费用信息 */
.trace-fee {
    background-color: #fffbe6;
    border-radius: 4px;
    padding: 7.5px;
    margin-bottom: 5px;
}

.fee-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2.5px;
}

.fee-row:last-child {
    margin-bottom: 0;
}

.fee-label {
    font-size: 12px;
    color: #999;
}

.fee-value {
    font-size: 12px;
    color: #333;
    font-weight: 600;
}

/* 备注 */
.trace-remark {
    font-size: 12px;
    color: #999;
    font-style: italic;
}

/* 订单详情区域 */
.order-detail-section {
    background-color: #fff;
    margin: 10px 15px;
    border-radius: 6px;
    padding: 12.5px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.detail-card .detail-row {
    padding: 7.5px 0;
    border-bottom: 1px solid #f0f0f0;
}

.detail-card .detail-row:last-child {
    border-bottom: none;
}

.patient-info {
    display: flex;
    align-items: center;
    gap: 5px;
}

.patient-tag {
    font-size: 11px;
    color: #07c160;
    background-color: #e8f8f0;
    padding: 2px 6px;
    border-radius: 3px;
}

.price {
    color: #333;
    font-weight: 600;
}

.price-red {
    color: #ff3b30;
    font-weight: 600;
}

.price-green {
    color: #07c160;
    font-weight: 600;
}

/* 多退少补按钮 */
.action-btn-area {
    margin-top: 15px;
}

.refund-btn {
    width: 100%;
    background-color: #40c9a2;
    color: #fff;
    border-radius: 6px;
    font-size: 16px;
    border: none;
    padding: 10px;
}

.refund-btn.green {
    background-color: #40c9a2;
}

/* 缴费记录 */
.payment-section {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f0f0f0;
}

.payment-title {
    font-size: 13px;
    color: #999;
    margin-bottom: 7.5px;
}

.payment-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 0;
}

.payment-time {
    font-size: 12px;
    color: #666;
}

.payment-amount {
    font-size: 13px;
    color: #07c160;
    font-weight: 600;
}

/* 导航栏 */
.nav-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 44px;
    padding: 0 15px;
    background-color: #fff;
    border-bottom: 1px solid #eee;
}

.nav-bar-placeholder {
    height: 44px;
}

.nav-back {
    display: flex;
    align-items: center;
    gap: 2.5px;
    cursor: pointer;
}

.back-arrow {
    font-size: 16px;
    color: #07c160;
}

.back-text {
    font-size: 14px;
    color: #07c160;
}

.nav-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.nav-placeholder {
    width: 50px;
}

.payTypes {
    width: 96%;
    padding: 15px;
    border-radius: 10px;
    background: white;
}

.payType-title {
    border-bottom: 1px solid #cccccc;
    height: 30px;
    line-height: 30px;
    padding: 0 15px;
    color: #292929;
}

.payType-bottom {
    border-bottom: 1px solid #cccccc;
}

.payType-radio /deep/ .el-radio {
    width: 100%;
    padding: 0 20px;
    height: 50px;
    line-height: 50px;
    box-sizing: border-box;
}

.payType-radio /deep/ .el-radio__label {
    display: flex;
    font-size: 16px;
    align-items: center;
}

.payType-radio /deep/ .el-radio__input {
    float: right;
    display: flex;
    align-items: center;
    height: 100%;
}


.imgIcon {
    width: 25px;
    height: 25px;
    padding: 0 5px;
}

.trace-detail {
    background-color: #f9f9f9;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
}

.detail-content {
    flex: 1;
}

.detail-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
}

.detail-row:last-child {
    margin-bottom: 0;
}

.detail-label {
    font-size: 13px;
    color: #101010;
    width: 60px;
    flex-shrink: 0;
}

.detail-value {
    font-size: 13px;
    color: #333;
    flex: 1;
    margin-left: 5px;
}

.detail-fee {
    width: 90px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
}

.fee-row {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-bottom: 6px;
}

.fee-row:last-child {
    margin-bottom: 0;
}

.fee-label {
    font-size: 11px;
    color: #999;
    margin-bottom: 6px;
}

.fee-value {
    font-size: 13px;
    font-weight: 600;
    color: #999;
}

.fee-value.red {
    color: #ff3b30;
}

.fee-value.green {
    color: #07c160;
}

.remark-text {
    font-size: 12px;
    color: #A9A9A9;
}

.detail-value.green {
    color: rgba(0, 204, 163, 0.72);
}

.detail-value.red {
    color: #ff3b30;
}

.fee-hint {
    font-size: 12px;
    color: #999;
}

.fee-note {
    padding: 6px 0 5px;
    border-top: 1px dashed #e0e0e0;
    margin-top: 4px;
}

.fee-note span {
    font-size: 12px;
    color: #999;
}
</style>
